import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, CornerDownLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { visibleToolsFor } from '../pages/tools/toolsList';
import { readToolsMru, pushToolsMru } from '../pages/tools/toolsMru';
import { toolSearchEntries } from '../pages/tools/toolSearch';
import ToolMonogram from '../toolbuilder/ToolMonogram';
import { useSidebarTools } from '../toolbuilder/sidebarTools';
import { trackFeatureEvent } from '../utils/featureEvents';

/* ─── Tools quick search — a small fixed icon in the top-right of every
   /tools page. Click (or Ctrl/Cmd+K) expands it LEFTWARD into a ~30%-width
   search bar that OVERLAYS the page (fixed + portal — never pushes layout),
   with a results dropdown: tool card art + name + description, ArrowUp/Down +
   Enter to jump, Esc to close. Recently opened tools rank first on an empty
   query (localStorage MRU). Mounted once at App level, like AcpQuickView.
   The workspace CommandPalette yields Ctrl+K on /tools routes.
   The person's own saved tools (Build a tool) are listed too (2026-10-02),
   in the Tools rail's order and with their monogram — pages/tools/toolSearch.js. ─── */

const FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const EASE = [0.16, 1, 0.3, 1];

// Visibility is toolsList.isToolVisible — the one rule shared with ToolsHome
// and the Tools rail. Recents come from toolsMru.js, which every tool visit
// feeds (not just openings made from this search).

export default function ToolsQuickSearch() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const onTools = location.pathname.startsWith('/tools');

  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const rootRef = useRef(null);

  const visibleTools = useMemo(() => visibleToolsFor(user), [user]);
  // Saved tools: the Tools rail's own list (mounted on every page, so it loads only on /tools).
  const saved = useSidebarTools(user?._id, { enabled: onTools });

  // Official and saved tools in the rail's order; on an empty query recently opened first. The MRU
  // is re-read whenever the dropdown opens.
  const results = useMemo(
    () => toolSearchEntries({ official: visibleTools, rail: saved.tools, available: saved.available, query, mru: readToolsMru() }),
    [query, visibleTools, saved.tools, saved.available, expanded] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const recentIds = useMemo(() => (query.trim() ? [] : readToolsMru()), [query, expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = useCallback(() => { setExpanded(false); setQuery(''); setHighlight(0); }, []);

  const openTool = useCallback((t) => {
    pushToolsMru(t.key);
    if (t.kind === 'mine') {
      trackFeatureEvent('tool.open', { target: t.id, label: t.name, meta: { via: 'search', mine: t.mine } });
      navigate(`/tools/my/${t.id}`);
    } else {
      navigate(`/tools/${t.id}`);
    }
    close();
  }, [navigate, close]);

  // Ctrl/Cmd+K toggles — only on /tools routes (the workspace CommandPalette
  // ignores the shortcut there, see CommandPaletteContext).
  useEffect(() => {
    if (!onTools) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setExpanded((x) => !x);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onTools]);

  // Focus the input when expanding; reset highlight when results change.
  useEffect(() => { if (expanded) setTimeout(() => inputRef.current?.focus(), 30); }, [expanded]);
  useEffect(() => { setHighlight(0); }, [query]);

  // Click outside closes.
  useEffect(() => {
    if (!expanded) return;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) close(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [expanded, close]);

  if (!onTools) return null;

  const onInputKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = results[highlight];
      if (picked) openTool(picked);
    }
  };

  const currentToolId = location.pathname.replace(/^\/tools\/?/, '').split('/')[0] || null;
  const currentUserToolId = /^\/tools\/my\/([^/]+)/.exec(location.pathname)?.[1] || null;

  return createPortal(
    <div ref={rootRef} className="fixed top-3 right-4 z-[70]" style={{ fontFamily: FONT }}>
      <div className="flex items-center justify-end">
        <AnimatePresence>
          {expanded && (
            <motion.div
              key="bar"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 'min(30vw, 460px)', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: EASE }}
              className="overflow-hidden"
            >
              <div className="relative mr-1.5" style={{ minWidth: 240 }}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9e9ea0] dark:text-white/30 pointer-events-none" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onInputKeyDown}
                  placeholder="Jump to a tool…"
                  className="w-full h-9 pl-9 pr-8 rounded-full border border-[#cacacb] dark:border-white/[0.15] bg-white dark:bg-[#161616] text-[#111111] dark:text-white text-[12.5px] placeholder:text-[#9e9ea0] dark:placeholder:text-white/30 focus:outline-none focus:border-[#111111] dark:focus:border-white shadow-[0_8px_24px_rgba(0,0,0,0.10)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.5)] transition-colors"
                />
                <button
                  onClick={close}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-[#9e9ea0] dark:text-white/35 hover:text-[#111111] dark:hover:text-white transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          onClick={() => (expanded ? close() : setExpanded(true))}
          title="Search tools (Ctrl+K)"
          className={`w-9 h-9 rounded-full border flex items-center justify-center shrink-0 transition-colors shadow-[0_4px_16px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_16px_rgba(0,0,0,0.4)] ${
            expanded
              ? 'bg-[#111111] dark:bg-white border-[#111111] dark:border-white text-white dark:text-[#111111]'
              : 'bg-white dark:bg-[#161616] border-[#cacacb] dark:border-white/[0.15] text-[#707072] dark:text-white/50 hover:text-[#111111] dark:hover:text-white hover:border-[#111111] dark:hover:border-white'
          }`}
        >
          <Search className="w-4 h-4" strokeWidth={2.25} />
        </button>
      </div>

      {/* Results dropdown — absolute under the bar, overlays the page */}
      {expanded && (
        <div
          className="absolute right-0 top-11 border border-[#cacacb] dark:border-white/[0.15] bg-white dark:bg-[#0A0A0B] shadow-[0_16px_48px_rgba(0,0,0,0.16)] dark:shadow-[0_16px_48px_rgba(0,0,0,0.6)] overflow-hidden"
          style={{ width: 'min(30vw, 460px)', minWidth: 280 }}
        >
          <div className="max-h-[min(430px,60vh)] overflow-y-auto scrollbar-overlay">
            {results.length === 0 && (
              <p className="px-3.5 py-3 text-[12px] text-[#9e9ea0] dark:text-white/35">No tools match “{query}”.</p>
            )}
            {results.map((t, i) => (
              <button
                key={t.key}
                onClick={() => openTool(t)}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left border-b last:border-b-0 border-[#f0f0f0] dark:border-white/[0.05] transition-colors ${
                  i === highlight ? 'bg-[#f5f5f5] dark:bg-white/[0.06]' : ''
                }`}
              >
                {/* Mini card art — same image as the ToolsHome tile; a saved tool wears its monogram */}
                <span className="w-9 h-9 shrink-0 overflow-hidden bg-[#f5f5f5] dark:bg-[#161616] ring-1 ring-[#e5e5e5] dark:ring-white/[0.06] flex items-center justify-center">
                  {t.kind === 'mine'
                    ? <ToolMonogram name={t.name} size={14} className="text-[#707072] dark:text-white/50" />
                    : t.image
                      ? <img src={t.image} alt="" className="w-full h-full object-cover" loading="lazy" />
                      : t.Icon ? <t.Icon className="w-4 h-4 text-[#707072] dark:text-white/50" /> : null}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-1.5">
                    <span className="text-[12.5px] font-bold text-[#111111] dark:text-white truncate">{t.name}</span>
                    {recentIds.includes(t.key) && (
                      <span className="text-[8px] font-bold uppercase tracking-[0.12em] text-[#9e9ea0] dark:text-white/30 border border-[#e5e5e5] dark:border-white/[0.1] px-1 py-px rounded-full shrink-0">Recent</span>
                    )}
                    {(t.kind === 'mine' ? t.id === currentUserToolId : t.id === currentToolId) && (
                      <span className="text-[8px] font-bold uppercase tracking-[0.12em] text-[#007d48] dark:text-[#1eaa52] border border-[#007d48]/25 dark:border-[#1eaa52]/30 px-1 py-px rounded-full shrink-0">Current</span>
                    )}
                  </span>
                  <span className="block text-[10.5px] text-[#9e9ea0] dark:text-white/35 truncate">{t.description}</span>
                </span>
                <span className="flex items-center gap-1 shrink-0">
                  {t.kind === 'mine' ? (
                    <span className="text-[8px] font-bold uppercase tracking-[0.08em] text-[#0E8578] dark:text-[#35C4B5]">{t.mine ? 'My tool' : 'Team'}</span>
                  ) : (t.supportedMarkets || ['com']).map((m) => (
                    <span key={m} className="text-[8px] font-bold uppercase tracking-[0.08em] text-[#9e9ea0] dark:text-white/30">{m}</span>
                  ))}
                  {i === highlight && <CornerDownLeft className="w-3 h-3 ml-1 text-[#707072] dark:text-white/45" />}
                </span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 px-3.5 py-1.5 border-t border-[#e5e5e5] dark:border-white/[0.08] text-[9px] font-bold uppercase tracking-[0.12em] text-[#9e9ea0] dark:text-white/30">
            <span>↑↓ Navigate</span><span>↵ Open</span><span>Esc Close</span><span className="ml-auto">Ctrl+K</span>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
